import { Helmet } from 'react-helmet-async';
import { useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Container,
  IconButton,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import { get, patch, post } from 'src/utils/networkUtils';
import PlayerTransferList from 'src/components/sync-players/PlayerTransferList';

function getMembershipSyncStatus(player) {
  if (!player.squad_membership_id) {
    return { label: 'No membership', color: 'warning' };
  }
  if (player.contestant_id === player.squad_membership_contestant_id) {
    return { label: 'In sync', color: 'success' };
  }
  return { label: 'Out of sync', color: 'warning' };
}

function mapPaulApiPlayer(player) {
  return {
    ...player,
    id: player.optaPlayerId,
    label:
      [player.shortFirstName, player.shortLastName].filter(Boolean).join(' ') ||
      player.optaPlayerId,
    searchText: [player.shortFirstName, player.shortLastName].filter(Boolean).join(' '),
    secondaryLabel: [
      `Position: ${player.position ?? '—'}`,
      `New price: ${player.newPrice ?? '—'}`,
      `Delta: ${
        player.delta === null || player.delta === undefined
          ? '—'
          : player.delta > 0
            ? `+${player.delta}`
            : player.delta
      }`,
    ].join(' · '),
  };
}

function CopyableId({ label, value }) {
  if (!value) return null;

  return (
    <Tooltip title="Click to copy" placement="top">
      <Typography
        component="button"
        type="button"
        variant="caption"
        onClick={() => navigator.clipboard?.writeText(value)}
        sx={{
          display: 'block',
          p: 0,
          border: 0,
          bgcolor: 'transparent',
          color: 'text.secondary',
          fontFamily: 'monospace',
          fontSize: '0.65rem',
          lineHeight: 1.5,
          cursor: 'pointer',
          textAlign: 'left',
          '&:hover': { color: 'primary.main', textDecoration: 'underline' },
        }}
      >
        {label}: {value}
      </Typography>
    </Tooltip>
  );
}

export default function SyncPlayersPage() {
  const [activeTab, setActiveTab] = useState('club-transfers');
  const [players, setPlayers] = useState(null);
  const [playersError, setPlayersError] = useState(null);
  const [playerSearch, setPlayerSearch] = useState('');
  const [contestants, setContestants] = useState(null);
  const [contestantsError, setContestantsError] = useState(null);
  const [editingPlayerId, setEditingPlayerId] = useState(null);
  const [selectedContestant, setSelectedContestant] = useState(null);
  const [savingPlayerId, setSavingPlayerId] = useState(null);
  const [syncingPlayerId, setSyncingPlayerId] = useState(null);
  const [editError, setEditError] = useState(null);
  const [paulApiPlayers, setPaulApiPlayers] = useState(null);
  const [paulApiPlayersError, setPaulApiPlayersError] = useState(null);
  const [paulApiPlayersLoading, setPaulApiPlayersLoading] = useState(false);
  const [playersToSync, setPlayersToSync] = useState([]);
  const [savingPlayersToSync, setSavingPlayersToSync] = useState(false);
  const [playersSyncError, setPlayersSyncError] = useState(null);
  const [playersSyncResult, setPlayersSyncResult] = useState(null);

  const normalizedSearch = playerSearch.trim().toLocaleLowerCase();
  const filteredPlayers = normalizedSearch
    ? (players?.filter(player =>
        [player.first_name, player.last_name, player.short_first_name, player.short_last_name].some(
          name => name?.toLocaleLowerCase().includes(normalizedSearch)
        )
      ) ?? [])
    : (players ?? []);

  useEffect(() => {
    if (activeTab !== 'club-transfers' || players !== null) return;

    get('/admin/opta/persons')
      .then(response => {
        setPlayers(response?.data ?? []);
        setPlayersError(null);
      })
      .catch(error => {
        setPlayers([]);
        setPlayersError(error?.response?.data?.message ?? error?.message ?? 'Load failed');
      });
  }, [activeTab, players]);

  useEffect(() => {
    if (!editingPlayerId || contestants !== null) return;

    get('/admin/opta/contestants?limit=500')
      .then(response => {
        setContestants(response?.data ?? []);
        setContestantsError(null);
      })
      .catch(error => {
        setContestants([]);
        setContestantsError(error?.response?.data?.message ?? error?.message ?? 'Load failed');
      });
  }, [editingPlayerId, contestants]);

  useEffect(() => {
    if (activeTab !== 'sync-players' || paulApiPlayers !== null) return;

    setPaulApiPlayersLoading(true);
    get('/admin/opta/pricing-feed/players')
      .then(response => {
        const apiPlayers = response?.data ?? [];
        setPaulApiPlayers(apiPlayers.map(mapPaulApiPlayer));
        setPaulApiPlayersError(null);
      })
      .catch(error => {
        setPaulApiPlayers([]);
        setPaulApiPlayersError(
          error?.response?.data?.message ?? error?.message ?? 'Failed to load Paul API players'
        );
      })
      .finally(() => setPaulApiPlayersLoading(false));
  }, [activeTab, paulApiPlayers]);

  const handleReloadPaulApiPlayers = async () => {
    setPaulApiPlayersLoading(true);
    setPaulApiPlayersError(null);
    try {
      const response = await get('/admin/opta/pricing-feed/players');
      const selectedIds = new Set(playersToSync.map(player => player.optaPlayerId));
      setPaulApiPlayers(
        (response?.data ?? [])
          .map(mapPaulApiPlayer)
          .filter(player => !selectedIds.has(player.optaPlayerId))
      );
    } catch (error) {
      setPaulApiPlayersError(
        error?.response?.data?.message ?? error?.message ?? 'Failed to reload Paul API players'
      );
    } finally {
      setPaulApiPlayersLoading(false);
    }
  };

  const handleEdit = player => {
    setEditingPlayerId(player.id);
    setSelectedContestant(
      player.contestant_id
        ? { id: player.contestant_id, name: player.contestant_name ?? 'Unknown contestant' }
        : null
    );
    setEditError(null);
  };

  const handleCancelEdit = () => {
    setEditingPlayerId(null);
    setSelectedContestant(null);
    setEditError(null);
  };

  const handleSaveContestant = async player => {
    if (!selectedContestant) {
      setEditError('Select a club before saving.');
      return;
    }
    const playerId = player.id;
    setSavingPlayerId(playerId);
    setEditError(null);
    try {
      const response = await patch(`/admin/opta/persons/${playerId}/contestant`, {
        contestant_id: selectedContestant.id,
        squad_membership_id: player.squad_membership_id ?? null,
        tournament_calendar_id: player.tournament_calendar_id ?? null,
      });
      const updatedAssignment = response?.data;
      setPlayers(currentPlayers =>
        currentPlayers.map(player =>
          player.id === playerId
            ? {
                ...player,
                contestant_id: selectedContestant.id,
                contestant_name: selectedContestant.name,
                squad_membership_contestant_id: selectedContestant.id,
                squad_membership_contestant_name: selectedContestant.name,
                squad_membership_id:
                  updatedAssignment?.squad_membership_id ?? player.squad_membership_id,
              }
            : player
        )
      );
      handleCancelEdit();
    } catch (error) {
      setEditError(error?.response?.data?.message ?? error?.message ?? 'Update failed');
    } finally {
      setSavingPlayerId(null);
    }
  };

  const handleSyncMembership = async player => {
    if (!player.contestant_id) {
      setEditError('Assign a contestant to the person before syncing the membership.');
      return;
    }

    setSyncingPlayerId(player.id);
    setEditError(null);
    try {
      const response = await patch(`/admin/opta/persons/${player.id}/contestant`, {
        contestant_id: player.contestant_id,
        squad_membership_id: player.squad_membership_id ?? null,
        tournament_calendar_id: player.tournament_calendar_id ?? null,
      });
      const updatedAssignment = response?.data;
      setPlayers(currentPlayers =>
        currentPlayers.map(currentPlayer =>
          currentPlayer.id === player.id
            ? {
                ...currentPlayer,
                squad_membership_contestant_id: player.contestant_id,
                squad_membership_contestant_name: player.contestant_name,
                squad_membership_id:
                  updatedAssignment?.squad_membership_id ?? currentPlayer.squad_membership_id,
              }
            : currentPlayer
        )
      );
    } catch (error) {
      setEditError(error?.response?.data?.message ?? error?.message ?? 'Sync failed');
    } finally {
      setSyncingPlayerId(null);
    }
  };

  const handleSavePlayersToSync = async () => {
    if (playersToSync.length === 0) return;

    setSavingPlayersToSync(true);
    setPlayersSyncError(null);
    setPlayersSyncResult(null);
    try {
      const response = await post('/admin/opta/pricing-feed/players/sync', {
        players: playersToSync.map(player => ({
          optaPlayerId: player.optaPlayerId,
          optaSquadId: player.optaSquadId,
          position: player.position,
          newPrice: player.newPrice,
        })),
      });
      setPlayersSyncResult(response?.data ?? null);
    } catch (error) {
      setPlayersSyncError(
        error?.response?.data?.message ?? error?.message ?? 'Failed to insert missing players'
      );
    } finally {
      setSavingPlayersToSync(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Sync Players & Transfers | EngageCraft Fantasy Football Admin</title>
      </Helmet>

      <Container maxWidth="xl">
        <Typography variant="h4" sx={{ mb: 1 }}>
          Sync Players & Transfers
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Sync players from Paul&apos;s API & Manage Club Transfers
        </Typography>

        <Card>
          <Box sx={{ px: 3, pt: 2 }}>
            <Tabs
              value={activeTab}
              onChange={(_, value) => setActiveTab(value)}
              aria-label="Sync players and transfers tabs"
            >
              <Tab label="Club Transfers" value="club-transfers" />
              <Tab label="Sync Players" value="sync-players" />
            </Tabs>
          </Box>

          {activeTab === 'club-transfers' && (
            <Box sx={{ p: 3 }}>
              {playersError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {playersError}
                </Alert>
              )}

              {editError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {editError}
                </Alert>
              )}

              <TextField
                fullWidth
                size="small"
                label="Search players"
                placeholder="Search by first, last, or short name"
                value={playerSearch}
                onChange={event => setPlayerSearch(event.target.value)}
                sx={{ mb: 2 }}
              />

              {players === null ? (
                <Box sx={{ py: 6, textAlign: 'center' }}>
                  <CircularProgress />
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small" aria-label="Players available to sync">
                    <TableHead>
                      <TableRow>
                        <TableCell>Player</TableCell>
                        <TableCell>Club / Contestant</TableCell>
                        <TableCell>Squad Membership Contestant</TableCell>
                        <TableCell>Membership Sync</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredPlayers.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                            {normalizedSearch ? 'No matching players found.' : 'No players found.'}
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredPlayers.map(player => (
                          <TableRow key={player.id} hover>
                            <TableCell>
                              <Typography variant="body2" fontWeight={600}>
                                {[player.first_name, player.last_name].filter(Boolean).join(' ') ||
                                  'Unnamed player'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {[player.short_first_name, player.short_last_name]
                                  .filter(Boolean)
                                  .join(' ') || 'No short name'}
                              </Typography>
                              <Box sx={{ mt: 0.5 }}>
                                <CopyableId label="Person ID" value={player.id} />
                                <CopyableId label="OPTA ID" value={player.opta_person_id} />
                              </Box>
                            </TableCell>
                            <TableCell sx={{ width: '40%' }}>
                              {editingPlayerId === player.id ? (
                                <Autocomplete
                                  size="small"
                                  options={contestants ?? []}
                                  value={selectedContestant}
                                  onChange={(_, value) => setSelectedContestant(value)}
                                  disableClearable
                                  getOptionLabel={option => option.name ?? ''}
                                  isOptionEqualToValue={(option, value) => option.id === value.id}
                                  loading={contestants === null}
                                  renderInput={params => (
                                    <TextField
                                      {...params}
                                      label="Club / Contestant"
                                      error={Boolean(contestantsError)}
                                      helperText={contestantsError}
                                    />
                                  )}
                                />
                              ) : (
                                (player.contestant_name ?? 'Unassigned')
                              )}
                            </TableCell>
                            <TableCell>
                              {player.squad_membership_contestant_name ?? 'No membership'}
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Chip
                                  size="small"
                                  label={getMembershipSyncStatus(player).label}
                                  color={getMembershipSyncStatus(player).color}
                                />
                                <Tooltip title="Sync membership to person contestant">
                                  <span>
                                    <IconButton
                                      size="small"
                                      aria-label="Sync membership to person contestant"
                                      disabled={
                                        !player.contestant_id ||
                                        player.contestant_id ===
                                          player.squad_membership_contestant_id ||
                                        syncingPlayerId === player.id
                                      }
                                      onClick={() => handleSyncMembership(player)}
                                    >
                                      <SyncIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </Box>
                            </TableCell>
                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                              {editingPlayerId === player.id ? (
                                <>
                                  <Button
                                    size="small"
                                    variant="contained"
                                    disabled={savingPlayerId === player.id}
                                    onClick={() => handleSaveContestant(player)}
                                  >
                                    {savingPlayerId === player.id ? 'Saving…' : 'Save'}
                                  </Button>
                                  <Button
                                    size="small"
                                    disabled={savingPlayerId === player.id}
                                    onClick={handleCancelEdit}
                                    sx={{ ml: 1 }}
                                  >
                                    Cancel
                                  </Button>
                                </>
                              ) : (
                                <Button size="small" onClick={() => handleEdit(player)}>
                                  Edit
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}

          {activeTab === 'sync-players' && (
            <Box sx={{ p: 3 }}>
              {paulApiPlayersError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {paulApiPlayersError}
                </Alert>
              )}
              {playersSyncError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {playersSyncError}
                </Alert>
              )}
              {playersSyncResult && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  {playersSyncResult.inserted} missing player
                  {playersSyncResult.inserted === 1 ? '' : 's'} inserted.{' '}
                  {playersSyncResult.alreadyExisted} already existed.{' '}
                  {playersSyncResult.membershipsInserted} membership link
                  {playersSyncResult.membershipsInserted === 1 ? '' : 's'} repaired.{' '}
                  {playersSyncResult.fantasyPlayersInserted} fantasy-player link
                  {playersSyncResult.fantasyPlayersInserted === 1 ? '' : 's'} repaired.{' '}
                  {playersSyncResult.fantasyPlayersUpdated} fantasy player
                  {playersSyncResult.fantasyPlayersUpdated === 1 ? '' : 's'} updated.{' '}
                  {playersSyncResult.personContestantsUpdated} person club assignment
                  {playersSyncResult.personContestantsUpdated === 1 ? '' : 's'} updated.{' '}
                  {playersSyncResult.membershipContestantsUpdated} membership club assignment
                  {playersSyncResult.membershipContestantsUpdated === 1 ? '' : 's'} updated.{' '}
                  {playersSyncResult.excessMembershipsDeleted} excess membership
                  {playersSyncResult.excessMembershipsDeleted === 1 ? '' : 's'} removed.
                </Alert>
              )}
              {paulApiPlayers === null ? (
                <Box sx={{ py: 6, textAlign: 'center' }}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  <PlayerTransferList
                    sourceItems={paulApiPlayers}
                    selectedItems={playersToSync}
                    onChange={({ sourceItems, selectedItems }) => {
                      setPaulApiPlayers(sourceItems);
                      setPlayersToSync(selectedItems);
                      setPlayersSyncError(null);
                      setPlayersSyncResult(null);
                    }}
                    onReloadSource={handleReloadPaulApiPlayers}
                    sourceReloading={paulApiPlayersLoading}
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <Button
                      variant="contained"
                      disabled={playersToSync.length === 0 || savingPlayersToSync}
                      onClick={handleSavePlayersToSync}
                    >
                      {savingPlayersToSync ? 'Saving…' : 'Save Players'}
                    </Button>
                  </Box>
                </>
              )}
            </Box>
          )}
        </Card>
      </Container>
    </>
  );
}
